import { useRef, useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Hero } from '../components/Hero';
import { ClientLogosCarousel } from '../components/ClientLogosCarousel';
import { GreetingPopup } from '../components/GreetingPopup';
import * as Icons from 'lucide-react';
import { Link } from 'react-router';
import { TestimonialCard } from '../components/TestimonialCard';
import { useSiteConfig, resolveUploadUrl } from '../utils/siteConfig';

export function Home() {
  const { config } = useSiteConfig();
  const services = config.services.slice(0, 3);
  const portfolio = config.portfolio.slice(0, 3);
  const originalCount = config.testimonials.length;

  const displayTestimonials = [
    ...config.testimonials.slice(-3),
    ...config.testimonials,
    ...config.testimonials.slice(0, 3)
  ];

  const [currentIndex, setCurrentIndex] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);

  const slide = (direction: 'left' | 'right') => {
    if (animating) return;
    setAnimating(true);
    setIsTransitioning(true);
    setCurrentIndex(prev => direction === 'right' ? prev + 1 : prev - 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= originalCount + 3) {
      setIsTransitioning(false);
      setCurrentIndex(3);
    } else if (currentIndex <= 2) {
      setIsTransitioning(false);
      setCurrentIndex(originalCount + 2);
    }
    setAnimating(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsHovered(true);
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsHovered(false);
    if (touchStartX.current === null) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        slide('right');
      } else {
        slide('left');
      }
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  useEffect(() => {
    if (isHovered) return;

    const timer = setInterval(() => {
      slide('right');
    }, 4000);

    return () => clearInterval(timer);
  }, [isHovered]);

  return (
    <div className="bg-slate-50">
      <Hero />
      <ClientLogosCarousel logos={config.home.hero.clientLogos} title="Trusted by 600+ Brands" />
      
      {/* Featured Services Section */}
      <section className="py-12 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex justify-between items-end mb-12">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 heading-glow">What We Do</h2>
            <p className="text-lg text-slate-600 max-w-2xl">We offer comprehensive digital solutions tailored to accelerate your growth.</p>
          </div>
          <Link to="/services" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:text-accent transition-colors">
            View All Services <Icons.ArrowRight size={20} />
          </Link>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = (Icons as Record<string, any>)[service.icon] || Icons.Zap;
            return (
              <motion.div
                key={index}
                className="rounded-[2rem] overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Image or icon fallback */}
                <div className="h-44 overflow-hidden relative bg-slate-50 border-b border-slate-100">
                  {service.image ? (
                    <img src={resolveUploadUrl(service.image)} alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/15 rounded-xl flex items-center justify-center">
                        <IconComponent size={24} className="text-primary" />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-6 flex-grow flex flex-col justify-between bg-white">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center text-primary shadow-sm shrink-0">
                        <IconComponent size={16} />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">{service.title}</h3>
                    </div>
                    <p className="text-slate-500 text-xs leading-relaxed font-sans line-clamp-4">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-8 text-center md:hidden">
          <Link to="/services" className="inline-flex items-center gap-2 text-primary font-semibold hover:text-accent transition-colors">
            View All Services <Icons.ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Featured Portfolio Section */}
      <section className="py-12 md:py-24 px-4 md:px-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 heading-glow">Selected Work</h2>
              <p className="text-lg text-slate-600 max-w-2xl">A glimpse into the digital experiences we've crafted for our partners.</p>
            </div>
            <Link to="/portfolio" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:text-accent transition-colors">
              View Portfolio <Icons.ArrowRight size={20} />
            </Link>
          </div>
          
          <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-8 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
            {portfolio.map((project, index: number) => (
              <motion.div
                key={index}
                onClick={() => setSelectedProject(project)}
                className="w-[85vw] sm:w-[340px] md:w-auto shrink-0 snap-center rounded-[2rem] overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col cursor-pointer hover:-translate-y-1.5"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {project.image ? (
                  <div className="h-52 overflow-hidden relative">
                    <img 
                      src={resolveUploadUrl(project.image)} 
                      alt={project.title}  
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" 
                    />
                  </div>
                ) : (
                  <div className="h-4 bg-slate-50 border-b border-slate-100" />
                )}
                
                <div className="p-6 flex-grow flex flex-col justify-between bg-white">
                  <div>
                    {/* Category */}
                    <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest block mb-2">
                      {project.category}
                    </span>
                    
                    {/* Title & Tags */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-black text-slate-900 leading-snug">
                        {project.title}
                      </h3>
                      
                      {project.tags && (
                        <div className="flex flex-wrap gap-1.5">
                          {project.tags.split(',').map((tag, tIdx) => (
                            <span 
                              key={tIdx} 
                              className="px-2.5 py-0.5 border border-slate-200 text-slate-500 rounded-full text-[10px] font-semibold tracking-wide"
                            >
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-slate-500 text-xs mt-3 line-clamp-2 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-primary font-semibold hover:text-accent transition-colors">
              View Portfolio <Icons.ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      <section className="py-12 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 heading-glow">Client Love</h2>
            <p className="text-lg text-slate-600 max-w-2xl">Don't just take our word for it.</p>
          </div>
          {/* Carousel Controls */}
          <div className="flex gap-3 shrink-0">
            <button 
              onClick={() => slide('left')}
              className="w-12 h-12 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300 active:scale-95 cursor-pointer"
              aria-label="Previous testimonials"
            >
              <Icons.ChevronLeft size={20} />
            </button>
            <button 
              onClick={() => slide('right')}
              className="w-12 h-12 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300 active:scale-95 cursor-pointer"
              aria-label="Next testimonials"
            >
              <Icons.ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden w-full relative">
          <div 
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTransitionEnd={handleTransitionEnd}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ 
              transform: `translateX(calc(-${currentIndex} * var(--slide-shift)))`,
              transition: isTransitioning ? 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)' : 'none'
            }}
            className="flex gap-6 carousel-track pb-8 pt-4"
          >
            {displayTestimonials.map((testimonial, index: number) => (
              <motion.div
                key={index}
                className="w-full min-w-full md:min-w-[calc(33.333%_-_16px)] md:w-[calc(33.333%_-_16px)] shrink-0 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % originalCount) * 0.02 }}
              >
                {testimonial.image ? (
                  <div className="p-8 rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-lg flex flex-col justify-between h-full hover:-translate-y-1.5 transition-all duration-300">
                    <div>
                      <div className="flex gap-1 text-primary mb-4">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <blockquote className="text-slate-700 text-sm md:text-[15px] leading-relaxed mb-4 font-sans whitespace-normal break-words text-wrap">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>
                    <div className="flex items-center gap-3 mt-auto">
                      <img src={testimonial.image} alt={testimonial.author} className="w-10 h-10 rounded-full object-cover bg-slate-200 shadow-sm shrink-0" />
                      <div>
                        <div className="font-bold text-slate-900 text-sm md:text-[15px]">{testimonial.author}</div>
                        <div className="text-xs text-slate-500 font-sans">{testimonial.role} {testimonial.company && `at ${testimonial.company}`}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full">
                    <TestimonialCard {...testimonial} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/65 backdrop-blur-md"
          onClick={() => setSelectedProject(null)}
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[2.5rem] overflow-hidden shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col md:flex-row relative border border-slate-100"
          >
            {/* Back Button */}
            <button 
              onClick={() => setSelectedProject(null)}
              className="absolute top-6 left-6 z-10 px-5 py-2.5 bg-white/90 backdrop-blur-md hover:bg-slate-50 text-slate-800 rounded-full border border-slate-200/80 shadow-md hover:shadow-lg transition-all flex items-center gap-2 font-semibold text-sm cursor-pointer"
            >
              <Icons.ArrowLeft size={16} /> Back
            </button>

            {/* Left/Top: Large Image */}
            <div className="w-full md:w-3/5 h-48 sm:h-64 md:h-auto min-h-[220px] sm:min-h-[300px] md:min-h-[500px] relative bg-slate-100 shrink-0">
              {selectedProject.image ? (
                <img 
                  src={resolveUploadUrl(selectedProject.image)} 
                  alt={selectedProject.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  No Image Available
                </div>
              )}
            </div>

            {/* Right/Bottom: Content Details */}
            <div className="w-full md:w-2/5 p-8 md:p-12 overflow-y-auto flex flex-col justify-between bg-white max-h-[90vh] md:max-h-auto">
              <div className="space-y-6 pt-8 md:pt-0">
                <span className="text-xs font-bold text-emerald-500 uppercase tracking-widest block">
                  {selectedProject.category}
                </span>
                
                <h3 className="text-3xl font-black text-slate-900 leading-tight">
                  {selectedProject.title}
                </h3>
                
                {selectedProject.tags && (
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.split(',').map((tag: string, tIdx: number) => (
                      <span 
                        key={tIdx} 
                        className="px-3.5 py-1 border border-slate-200 text-slate-600 rounded-full text-xs font-semibold"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
                
                <hr className="border-slate-100" />
                
                <div>
                  <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Project Overview</h4>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed font-sans">
                    {selectedProject.description}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100 flex gap-4">
                <button 
                  onClick={() => setSelectedProject(null)}
                  className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all text-center cursor-pointer"
                >
                  Close Project
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <GreetingPopup />
    </div>
  );
}
